"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";

const heroSlides = [
  {
    tag: "WE BUILD DIGITAL EXCELLENCE",
    title: "Empowering Businesses with Digital Solutions That Drive Growth",
    description:
      "Eratec Solution delivers website development, SEO, digital marketing, ecommerce and mobile app solutions designed to help businesses grow online.",
    primary: "GET FREE CONSULTATION",
    secondary: "VIEW OUR WORK",
  },
  {
    tag: "SEO & DIGITAL GROWTH",
    title: "Get Found. Get Leads. Grow Your Business Online.",
    description:
      "Build a stronger online presence with strategic SEO, Google Ads, social media marketing and performance-driven digital campaigns.",
    primary: "GET FREE CONSULTATION",
    secondary: "EXPLORE SEO SERVICES",
  },
  {
    tag: "WEB • ECOMMERCE • MOBILE",
    title: "Turn Your Digital Ideas Into High-Performance Experiences",
    description:
      "From business websites and ecommerce stores to custom mobile apps, we create digital experiences built for performance and conversions.",
    primary: "START YOUR PROJECT",
    secondary: "VIEW OUR SERVICES",
  },
];

export default function HomeHeroSlider() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const slide = heroSlides[activeSlide];

  const goPrev = () => {
    setActiveSlide(
      (prev) => (prev - 1 + heroSlides.length) % heroSlides.length
    );
  };

  const goNext = () => {
    setActiveSlide((prev) => (prev + 1) % heroSlides.length);
  };

  return (
    <section className="eratec-hero-slider">
      <div className="hero-glow hero-glow-one"></div>
      <div className="hero-glow hero-glow-two"></div>

      <div className="hero-grid"></div>

      <div className="container hero-container">
        <div className="row align-items-center">

          {/* LEFT CONTENT */}
          <div className="col-lg-6 col-md-12">
            <div
              className="hero-text"
              key={activeSlide}
            >
              <span className="hero-tag">
                {slide.tag}
              </span>

              <h1>
                {slide.title}
              </h1>

              <p>
                {slide.description}
              </p>

              <div className="hero-buttons">

                <Link
                  href="/contact"
                  className="hero-primary-btn"
                >
                  {slide.primary}
                  <span>→</span>
                </Link>

                <Link
                  href="/portfolio"
                  className="hero-secondary-btn"
                >
                  {slide.secondary}
                  <span>↗</span>
                </Link>

              </div>

              <div className="hero-trust">

                <div className="trust-item">
                  <strong>250+</strong>
                  <span>Businesses Served</span>
                </div>

                <div className="trust-line"></div>

                <div className="trust-item">
                  <strong>4.9 ★</strong>
                  <span>Google Rating</span>
                </div>

              </div>
            </div>
          </div>

          {/* RIGHT AI VISUAL */}
          <div className="col-lg-6 col-md-12">
            <div
              className="ai-visual"
              key={`visual-${activeSlide}`}
            >

              <div className="ai-ring ai-ring-one"></div>
              <div className="ai-ring ai-ring-two"></div>

              <div className="ai-orbit orbit-one">
                <span>AI</span>
              </div>

              <div className="ai-orbit orbit-two">
                <span>SEO</span>
              </div>

              <div className="ai-orbit orbit-three">
                <span>WEB</span>
              </div>

              <div className="ai-core">

                <div className="brain">
                  <div className="brain-half left-brain">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>

                  <div className="brain-center"></div>

                  <div className="brain-half right-brain">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>

                <div className="core-lines"></div>

              </div>

              <div className="ai-card card-one">
                <span>↗</span>
                <div>
                  <strong>Growth</strong>
                  <small>Digital Strategy</small>
                </div>
              </div>

              <div className="ai-card card-two">
                <span>⌘</span>
                <div>
                  <strong>Technology</strong>
                  <small>Smart Solutions</small>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* STATS */}
        <div className="hero-stats">

          <div className="hero-stat">
            <div className="stat-icon">✦</div>
            <div>
              <strong>5+ Years</strong>
              <span>Industry Experience</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="hero-stat">
            <div className="stat-icon">♟</div>
            <div>
              <strong>250+</strong>
              <span>Happy Clients</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="hero-stat">
            <div className="stat-icon">▣</div>
            <div>
              <strong>500+</strong>
              <span>Projects Delivered</span>
            </div>
          </div>

          <div className="stat-divider"></div>

          <div className="hero-stat">
            <div className="stat-icon">◉</div>
            <div>
              <strong>24/7</strong>
              <span>Support</span>
            </div>
          </div>

        </div>

        {/* SLIDER CONTROLS */}
        <div className="hero-controls">

          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
          >
            ←
          </button>

          <div className="hero-dots">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                className={activeSlide === index ? "active" : ""}
                onClick={() => setActiveSlide(index)}
              ></button>
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
          >
            →
          </button>

        </div>

      </div>

      <style jsx>{`

        .eratec-hero-slider {
          position: relative;
          overflow: hidden;
          min-height: 760px;
          background:
            radial-gradient(
              circle at 75% 45%,
              rgba(124, 58, 237, 0.22),
              transparent 35%
            ),
            linear-gradient(
              135deg,
              #05051c 0%,
              #0b0830 48%,
              #13083c 100%
            );
          color: #fff;
        }

        .hero-container {
          position: relative;
          z-index: 3;
          padding-top: 95px;
          padding-bottom: 55px;
        }

        .hero-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
        }

        .hero-glow-one {
          width: 400px;
          height: 400px;
          right: -100px;
          top: 100px;
          background: rgba(126, 34, 206, 0.28);
        }

        .hero-glow-two {
          width: 300px;
          height: 300px;
          left: -100px;
          bottom: 50px;
          background: rgba(67, 56, 202, 0.18);
        }

        .hero-grid {
          position: absolute;
          inset: 0;
          opacity: 0.12;
          background-image:
            linear-gradient(
              rgba(255,255,255,.08) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(255,255,255,.08) 1px,
              transparent 1px
            );
          background-size: 60px 60px;
        }

        .hero-text {
          animation: heroFade .7s ease;
        }

        @keyframes heroFade {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-tag {
          display: inline-block;
          padding: 8px 15px;
          border: 1px solid rgba(168, 85, 247, .45);
          border-radius: 50px;
          background: rgba(124, 58, 237, .12);
          color: #c084fc;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1px;
          margin-bottom: 25px;
        }

        .hero-text h1 {
          max-width: 700px;
          margin-bottom: 22px;
          font-size: clamp(42px, 5vw, 68px);
          line-height: 1.08;
          font-weight: 800;
          letter-spacing: -2px;
          color: #fff;
        }

        .hero-text h1::first-line {
          color: #fff;
        }

        .hero-text p {
          max-width: 620px;
          margin-bottom: 32px;
          color: #c9c7df;
          font-size: 17px;
          line-height: 1.75;
        }

        .hero-buttons {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }

        .hero-primary-btn,
        .hero-secondary-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
          min-height: 56px;
          padding: 0 25px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 700;
          transition: .3s ease;
        }

        .hero-primary-btn {
          color: #fff;
          background: linear-gradient(
            90deg,
            #7c3aed,
            #a855f7
          );
          box-shadow:
            0 12px 35px rgba(124,58,237,.35);
        }

        .hero-primary-btn:hover {
          transform: translateY(-3px);
          color: #fff;
          box-shadow:
            0 18px 45px rgba(168,85,247,.45);
        }

        .hero-secondary-btn {
          color: #fff;
          border: 1px solid rgba(168,85,247,.65);
          background: rgba(255,255,255,.03);
        }

        .hero-secondary-btn:hover {
          color: #fff;
          background: rgba(124,58,237,.12);
          transform: translateY(-3px);
        }

        .hero-trust {
          display: flex;
          align-items: center;
          gap: 25px;
          margin-top: 35px;
        }

        .trust-item {
          display: flex;
          flex-direction: column;
        }

        .trust-item strong {
          font-size: 21px;
          color: #fff;
        }

        .trust-item span {
          margin-top: 4px;
          color: #aaa7c2;
          font-size: 13px;
        }

        .trust-line {
          width: 1px;
          height: 45px;
          background: rgba(255,255,255,.18);
        }

        /* AI VISUAL */

        .ai-visual {
          position: relative;
          height: 530px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: visualIn .9s ease;
        }

        @keyframes visualIn {
          from {
            opacity: 0;
            transform: scale(.92);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .ai-ring {
          position: absolute;
          border: 1px solid rgba(168,85,247,.3);
          border-radius: 50%;
        }

        .ai-ring-one {
          width: 440px;
          height: 440px;
          animation: rotate 18s linear infinite;
        }

        .ai-ring-two {
          width: 340px;
          height: 340px;
          border-style: dashed;
          border-color: rgba(192,132,252,.35);
          animation: rotateReverse 12s linear infinite;
        }

        @keyframes rotate {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotateReverse {
          to {
            transform: rotate(-360deg);
          }
        }

        .ai-core {
          position: relative;
          width: 300px;
          height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background:
            radial-gradient(
              circle,
              rgba(168,85,247,.35),
              rgba(76,29,149,.15) 45%,
              transparent 70%
            );
          box-shadow:
            0 0 70px rgba(168,85,247,.3);
        }

        .brain {
          position: relative;
          width: 170px;
          height: 150px;
          display: flex;
          justify-content: center;
          filter: drop-shadow(
            0 0 15px rgba(192,132,252,.8)
          );
        }

        .brain-half {
          position: relative;
          width: 78px;
          height: 120px;
          border: 3px solid #d8b4fe;
          background:
            linear-gradient(
              135deg,
              rgba(168,85,247,.7),
              rgba(30,20,70,.8)
            );
        }

        .left-brain {
          border-radius: 55% 45% 45% 50%;
          transform: rotate(-5deg);
        }

        .right-brain {
          border-radius: 45% 55% 50% 45%;
          transform: rotate(5deg);
        }

        .brain-center {
          width: 5px;
          height: 130px;
          background: #f0abfc;
          box-shadow:
            0 0 15px #c084fc;
          z-index: 4;
        }

        .brain-half span {
          position: absolute;
          width: 35px;
          height: 3px;
          background: #e9d5ff;
          box-shadow:
            0 0 8px #c084fc;
        }

        .left-brain span:nth-child(1) {
          top: 30px;
          left: 18px;
          transform: rotate(20deg);
        }

        .left-brain span:nth-child(2) {
          top: 60px;
          left: 12px;
          transform: rotate(-15deg);
        }

        .left-brain span:nth-child(3) {
          top: 90px;
          left: 20px;
          transform: rotate(15deg);
        }

        .right-brain span:nth-child(1) {
          top: 30px;
          right: 18px;
          transform: rotate(-20deg);
        }

        .right-brain span:nth-child(2) {
          top: 60px;
          right: 12px;
          transform: rotate(15deg);
        }

        .right-brain span:nth-child(3) {
          top: 90px;
          right: 20px;
          transform: rotate(-15deg);
        }

        .ai-orbit {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 55px;
          height: 55px;
          border: 1px solid rgba(192,132,252,.7);
          border-radius: 14px;
          background: rgba(15,10,45,.8);
          box-shadow:
            0 0 25px rgba(168,85,247,.25);
          color: #d8b4fe;
          font-size: 12px;
          font-weight: 700;
        }

        .orbit-one {
          top: 70px;
          right: 55px;
        }

        .orbit-two {
          left: 45px;
          top: 200px;
        }

        .orbit-three {
          right: 65px;
          bottom: 75px;
        }

        .ai-card {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border: 1px solid rgba(168,85,247,.3);
          border-radius: 12px;
          background: rgba(12,8,38,.82);
          backdrop-filter: blur(15px);
          box-shadow:
            0 15px 35px rgba(0,0,0,.25);
        }

        .ai-card > span {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 9px;
          background: linear-gradient(
            135deg,
            #7c3aed,
            #c084fc
          );
          font-size: 18px;
        }

        .ai-card strong,
        .ai-card small {
          display: block;
        }

        .ai-card strong {
          color: #fff;
          font-size: 13px;
        }

        .ai-card small {
          margin-top: 3px;
          color: #aaa7c2;
          font-size: 10px;
        }

        .card-one {
          left: 5px;
          top: 100px;
        }

        .card-two {
          right: 0;
          bottom: 110px;
        }

        /* STATS */

        .hero-stats {
          position: relative;
          z-index: 5;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          margin-top: 20px;
          padding: 22px 30px;
          border: 1px solid rgba(168,85,247,.22);
          border-radius: 18px;
          background: rgba(255,255,255,.045);
          backdrop-filter: blur(15px);
        }

        .hero-stat {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
        }

        .stat-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          flex-shrink: 0;
          border-radius: 12px;
          background: linear-gradient(
            135deg,
            #6d28d9,
            #a855f7
          );
          color: #fff;
          font-size: 20px;
        }

        .hero-stat strong,
        .hero-stat span {
          display: block;
        }

        .hero-stat strong {
          color: #fff;
          font-size: 19px;
        }

        .hero-stat span {
          margin-top: 3px;
          color: #aaa7c2;
          font-size: 12px;
        }

        .stat-divider {
          width: 1px;
          height: 45px;
          align-self: center;
          background: rgba(255,255,255,.15);
        }

        /* CONTROLS */

        .hero-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 18px;
          margin-top: 25px;
        }

        .hero-controls > button {
          width: 38px;
          height: 38px;
          border: 1px solid rgba(168,85,247,.45);
          border-radius: 50%;
          background: rgba(255,255,255,.04);
          color: #fff;
          cursor: pointer;
          transition: .25s;
        }

        .hero-controls > button:hover {
          background: #7c3aed;
        }

        .hero-dots {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .hero-dots button {
          width: 8px;
          height: 8px;
          padding: 0;
          border: 0;
          border-radius: 20px;
          background: rgba(255,255,255,.3);
          cursor: pointer;
          transition: .3s;
        }

        .hero-dots button.active {
          width: 28px;
          background: #a855f7;
        }

        @media (max-width: 991px) {

          .eratec-hero-slider {
            min-height: auto;
          }

          .hero-container {
            padding-top: 65px;
          }

          .hero-text {
            text-align: center;
          }

          .hero-text h1,
          .hero-text p {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-buttons,
          .hero-trust {
            justify-content: center;
          }

          .ai-visual {
            height: 450px;
            margin-top: 20px;
          }

          .hero-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }

          .stat-divider {
            display: none;
          }
        }

        @media (max-width: 575px) {

          .hero-container {
            padding: 55px 18px 35px;
          }

          .hero-text h1 {
            font-size: 39px;
            line-height: 1.12;
            letter-spacing: -1px;
          }

          .hero-text p {
            font-size: 15px;
          }

          .hero-primary-btn,
          .hero-secondary-btn {
            width: 100%;
          }

          .hero-trust {
            gap: 15px;
          }

          .ai-visual {
            height: 350px;
          }

          .ai-ring-one {
            width: 300px;
            height: 300px;
          }

          .ai-ring-two {
            width: 240px;
            height: 240px;
          }

          .ai-core {
            width: 220px;
            height: 220px;
          }

          .brain {
            transform: scale(.78);
          }

          .ai-card {
            transform: scale(.78);
          }

          .card-one {
            left: -15px;
            top: 45px;
          }

          .card-two {
            right: -15px;
            bottom: 35px;
          }

          .orbit-one {
            right: 10px;
            top: 35px;
          }

          .orbit-two {
            left: 0;
            top: 140px;
          }

          .orbit-three {
            right: 15px;
            bottom: 20px;
          }

          .hero-stats {
            grid-template-columns: 1fr;
            padding: 18px;
          }

          .hero-stat {
            justify-content: flex-start;
          }
        }

      `}</style>
    </section>
  );
}